import React, { useCallback, useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import MapScanAuthModal from "../Components/MapScanAuthModal";
import locationMapDesktopLanding from "../Assets/Images/locationMapDesktopLanding.jpg";
import "./LocationMap.css";
import {
  authenticateMapScanUser,
  clearMapScanModals,
  clearMapScanToken,
  getCurrentMapScanUser,
  getListenedAudioMarkerIdsFromScans,
  getMarkerByModalProductId,
  getVisibleLocationMarkers,
  getMapScanToken,
  getMapScanModals,
  getMarkerIdsFromScans,
  LOCATION_CONFIG,
  LOCATION_MAP_REDIRECT_TOAST_KEY,
  markAudioMarkerAsListened,
  markMarkerAsScanned,
  readListenedAudioMarkers,
  readScannedMarkers,
  saveMapScanModal,
  setMapScanToken,
  writeListenedAudioMarkers,
  writeScannedMarkers,
} from "../Helpers/mapScan";

const ANCHOR_CATEGORIES = {
  green: { color: "#3fae54", label: "Garden Sculpture" },
  purple: { color: "#8e44c4", label: "Augmented Reality" },
  red: { color: "#e8463c", label: "Curious Sculpture" },
  blue: { color: "#2ea3e0", label: "Wonder Sculpture" },
};

const SCANNED_QR_COLOR = "#ec3d8f";
const DEFAULT_AUDIO_MARKER_COLOR = "#2ea3e0";
const LISTENED_AUDIO_MARKER_COLOR = "#3fae54";

function getSculptureTypeColor(sculptureType) {
  const normalizedType = String(sculptureType || "").toLowerCase();

  if (normalizedType.includes("character")) {
    return "#2ea3e0";
  }

  if (normalizedType.includes("floral")) {
    return "#3fae54";
  }

  if (normalizedType.includes("venus")) {
    return "#f38b2f";
  }

  return "#2ea3e0";
}

function getMarkerViewedState(
  marker,
  scannedMarkerIds = [],
  listenedAudioMarkerIds = []
) {
  const isScanned = scannedMarkerIds.includes(marker.id);
  const isQrMarker = marker.markerType === "qr";
  const isAudioListened = listenedAudioMarkerIds.includes(marker.id);

  return {
    color: isQrMarker
      ? isScanned
        ? SCANNED_QR_COLOR
        : ANCHOR_CATEGORIES.purple.color
      : isAudioListened
        ? LISTENED_AUDIO_MARKER_COLOR
        : DEFAULT_AUDIO_MARKER_COLOR,
    label: isQrMarker ? marker.qrLabel || "" : "",
    markerType: marker.markerType,
  };
}

function loadGoogleMapsScript(apiKey) {
  if (window.google && window.google.maps) {
    return Promise.resolve(window.google.maps);
  }

  const existingScript = document.getElementById("actuality-google-maps-script");
  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener("load", () => resolve(window.google.maps));
      existingScript.addEventListener("error", reject);
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = "actuality-google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

function createAnchorMarkerOverlay({ map, markerId, onClick, position, viewed }) {
  const overlay = new window.google.maps.OverlayView();
  overlay.markerId = markerId;
  overlay.position = position;
  overlay.viewed = viewed;
  overlay.container = null;

  overlay.onAdd = function onAdd() {
    const container = document.createElement("button");
    const body = document.createElement("span");
    const label = document.createElement("span");

    container.className = "anchor-map-pin";
    if (this.markerId === "ar-6") {
      container.classList.add("anchor-map-pin-ar-6");
    }
    container.type = "button";
    container.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      onClick();
    });

    body.className = "anchor-map-pin-body";
    label.className = "anchor-map-pin-label";
    container.appendChild(body);
    body.appendChild(label);

    this.container = container;
    this.updateViewed(this.viewed);
    this.getPanes().overlayMouseTarget.appendChild(container);
  };

  overlay.draw = function draw() {
    if (!this.container) {
      return;
    }

    const projection = this.getProjection();
    const point = projection?.fromLatLngToDivPixel(
      new window.google.maps.LatLng(this.position)
    );

    if (!point) {
      return;
    }

    this.container.style.left = `${point.x}px`;
    this.container.style.top = `${point.y}px`;
  };

  overlay.onRemove = function onRemove() {
    if (this.container?.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }

    this.container = null;
  };

  overlay.updateViewed = function updateViewed(nextViewed) {
    this.viewed = nextViewed;

    if (!this.container) {
      return;
    }

    this.container.style.setProperty("--anchor-pin-color", nextViewed.color);
    this.container.classList.toggle(
      "anchor-map-pin-amenity",
      nextViewed.markerType === "qr"
    );
    this.container.classList.toggle(
      "anchor-map-pin-has-label",
      Boolean(nextViewed.label)
    );

    const labelElement = this.container.querySelector(".anchor-map-pin-label");
    if (labelElement) {
      labelElement.textContent = nextViewed.label || "";
    }
  };

  overlay.setMap(map);
  return overlay;
}

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  "AIzaSyDA30l6vrlZw3uCP9nQnzntysJU1U092F0";

export default function LocationMap() {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRefs = useRef({});
  const currentLocationMarkerRef = useRef(null);
  const currentLocationAccuracyRef = useRef(null);
  const qrInputRef = useRef(null);
  const scannerVideoRef = useRef(null);
  const scannerDetectorRef = useRef(null);
  const scannerCanvasRef = useRef(null);
  const scannerStreamRef = useRef(null);
  const scannerTimeoutRef = useRef(null);
  const sheetAudioRef = useRef(null);
  const scannedMarkerIdsRef = useRef([]);
  const listenedAudioMarkerIdsRef = useRef([]);
  const [scannedMarkerIds, setScannedMarkerIds] = useState([]);
  const [listenedAudioMarkerIds, setListenedAudioMarkerIds] = useState([]);
  const [selectedMarkerId, setSelectedMarkerId] = useState("");
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState("");
  const [scanErrorMessage, setScanErrorMessage] = useState("");
  const [appToastMessage, setAppToastMessage] = useState("");
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isStartingScanner, setIsStartingScanner] = useState(false);
  const [isSheetAudioPlaying, setIsSheetAudioPlaying] = useState(false);
  const [sheetAudioDuration, setSheetAudioDuration] = useState(0);
  const [sheetAudioTime, setSheetAudioTime] = useState(0);
  const [isResettingScans, setIsResettingScans] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const usePhoneLayout = true;
  const isCompactViewport = window.innerWidth <= 960;
  const [isDesktopViewport, setIsDesktopViewport] = useState(
    window.innerWidth > 960
  );

  const getDistanceInMeters = (firstPoint, secondPoint) => {
    const toRadians = (value) => (value * Math.PI) / 180;
    const earthRadius = 6371000;
    const latitudeDelta = toRadians(secondPoint.lat - firstPoint.lat);
    const longitudeDelta = toRadians(secondPoint.lng - firstPoint.lng);
    const latitudeOne = toRadians(firstPoint.lat);
    const latitudeTwo = toRadians(secondPoint.lat);
    const a =
      Math.sin(latitudeDelta / 2) * Math.sin(latitudeDelta / 2) +
      Math.cos(latitudeOne) *
        Math.cos(latitudeTwo) *
        Math.sin(longitudeDelta / 2) *
        Math.sin(longitudeDelta / 2);

    return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const visibleMarkers = getVisibleLocationMarkers(scannedMarkerIds);

  const syncScannedMarkers = async (token) => {
    const localMarkerIds = readScannedMarkers();
    const localListenedAudioMarkerIds = readListenedAudioMarkers();

    if (!token) {
      setScannedMarkerIds(localMarkerIds);
      setListenedAudioMarkerIds(localListenedAudioMarkerIds);
      return;
    }

    try {
      const data = await getMapScanModals(token);
      if (data.status === "success") {
        setScannedMarkerIds([
          ...new Set([...localMarkerIds, ...getMarkerIdsFromScans(data.scans)]),
        ]);
        setListenedAudioMarkerIds([
          ...new Set([
            ...localListenedAudioMarkerIds,
            ...getListenedAudioMarkerIdsFromScans(data.scans),
          ]),
        ]);
      } else {
        setScannedMarkerIds(localMarkerIds);
        setListenedAudioMarkerIds(localListenedAudioMarkerIds);
      }
    } catch (error) {
      setScannedMarkerIds(localMarkerIds);
      setListenedAudioMarkerIds(localListenedAudioMarkerIds);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsDesktopViewport(window.innerWidth > 960);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const token = getMapScanToken();

    if (!token) {
      setIsCheckingAuth(false);
      return;
    }

    getCurrentMapScanUser(token)
      .then((data) => {
        if (data.status === "success") {
          setIsAuthenticated(true);
          syncScannedMarkers(token);
        } else {
          clearMapScanToken();
        }
      })
      .catch(() => {
        clearMapScanToken();
      })
      .finally(() => {
        setIsCheckingAuth(false);
      });
  }, []);

  useEffect(() => {
    scannedMarkerIdsRef.current = scannedMarkerIds;
  }, [scannedMarkerIds]);

  useEffect(() => {
    listenedAudioMarkerIdsRef.current = listenedAudioMarkerIds;
  }, [listenedAudioMarkerIds]);

  useEffect(() => {
    setListenedAudioMarkerIds(readListenedAudioMarkers());
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    let isMounted = true;

    const createMapInstance = (targetElement) => {
      if (!targetElement) {
        return null;
      }

      const map = new window.google.maps.Map(targetElement, {
        center: LOCATION_CONFIG.center,
        gestureHandling: "greedy",
        zoom: LOCATION_CONFIG.zoom,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          {
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "poi",
            elementType: "labels.icon",
            stylers: [{ visibility: "off" }],
          },
        ],
      });

      visibleMarkers.forEach((marker) => {
        const mapMarker = createAnchorMarkerOverlay({
          map,
          markerId: marker.id,
          position: marker.position,
          viewed: getMarkerViewedState(
            marker,
            scannedMarkerIdsRef.current,
            listenedAudioMarkerIdsRef.current
          ),
          onClick: () => {
            if (marker.markerType === "qr") {
              handleOpenQrScanner();
              return;
            }

            setSelectedMarkerId(marker.id);
          },
        });

        if (!markerRefs.current[marker.id]) {
          markerRefs.current[marker.id] = [];
        }

        markerRefs.current[marker.id].push(mapMarker);
      });

      window.setTimeout(() => {
        if (!isMounted || !window.google?.maps) {
          return;
        }

        window.google.maps.event.trigger(map, "resize");
        map.setCenter(LOCATION_CONFIG.center);
        map.setZoom(LOCATION_CONFIG.zoom);
      }, 180);

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            if (!isMounted) {
              return;
            }

            const currentPosition = {
              lat: coords.latitude,
              lng: coords.longitude,
            };

            if (currentLocationMarkerRef.current) {
              currentLocationMarkerRef.current.setMap(null);
            }

            if (currentLocationAccuracyRef.current) {
              currentLocationAccuracyRef.current.setMap(null);
            }

            currentLocationAccuracyRef.current = new window.google.maps.Circle({
              center: currentPosition,
              fillColor: "#4da3ff",
              fillOpacity: 0.16,
              map,
              radius: Math.max(coords.accuracy || 0, 14),
              strokeColor: "#4da3ff",
              strokeOpacity: 0.3,
              strokeWeight: 1,
            });

            currentLocationMarkerRef.current = new window.google.maps.Marker({
              icon: {
                fillColor: "#2ea3e0",
                fillOpacity: 1,
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 7,
                strokeColor: "#ffffff",
                strokeWeight: 3,
              },
              map,
              position: currentPosition,
              zIndex: 25,
            });

            if (
              getDistanceInMeters(currentPosition, LOCATION_CONFIG.center) <= 2000
            ) {
              const locationAwareBounds = new window.google.maps.LatLngBounds();
              visibleMarkers.forEach((marker) => {
                locationAwareBounds.extend(marker.position);
              });
              locationAwareBounds.extend(currentPosition);
              map.fitBounds(locationAwareBounds, 36);
              window.google.maps.event.addListenerOnce(map, "idle", () => {
                if (map.getZoom() > 21) {
                  map.setZoom(21);
                }
              });
            }
          },
          () => {},
          {
            enableHighAccuracy: true,
            maximumAge: 60000,
            timeout: 12000,
          }
        );
      }

      return map;
    };

    loadGoogleMapsScript(GOOGLE_MAPS_API_KEY)
      .then(() => {
        if (!isMounted) {
          return;
        }

        mapInstanceRef.current = createMapInstance(mapRef.current);
      })
      .catch(() => {});

    return () => {
      isMounted = false;
      mapInstanceRef.current = null;
      if (currentLocationMarkerRef.current) {
        currentLocationMarkerRef.current.setMap(null);
        currentLocationMarkerRef.current = null;
      }
      if (currentLocationAccuracyRef.current) {
        currentLocationAccuracyRef.current.setMap(null);
        currentLocationAccuracyRef.current = null;
      }
      markerRefs.current = {};
    };
  }, [isAuthenticated, scannedMarkerIds]);

  useEffect(() => {
    Object.entries(markerRefs.current).forEach(([markerId, markers]) => {
      const markerMeta =
        visibleMarkers.find((marker) => marker.id === markerId) || null;

      if (!markerMeta) {
        return;
      }

      markers.forEach((marker) => {
        marker.updateViewed(
          getMarkerViewedState(
            markerMeta,
            scannedMarkerIds,
            listenedAudioMarkerIds
          )
        );
      });
    });
  }, [listenedAudioMarkerIds, scannedMarkerIds]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const token = getMapScanToken();
    if (!token) {
      return undefined;
    }

    const refreshScannedState = () => {
      syncScannedMarkers(token);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshScannedState();
      }
    };

    window.addEventListener("focus", refreshScannedState);
    window.addEventListener("pageshow", refreshScannedState);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", refreshScannedState);
      window.removeEventListener("pageshow", refreshScannedState);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const audioElement = sheetAudioRef.current;

    return () => {
      if (scannerTimeoutRef.current) {
        window.clearTimeout(scannerTimeoutRef.current);
      }

      if (scannerStreamRef.current) {
        scannerStreamRef.current.getTracks().forEach((track) => track.stop());
        scannerStreamRef.current = null;
      }

      if (audioElement) {
        audioElement.pause();
      }
    };
  }, []);

  useEffect(() => {
    if (!appToastMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setAppToastMessage("");
    }, 2600);

    return () => window.clearTimeout(timeoutId);
  }, [appToastMessage]);

  useEffect(() => {
    const redirectToastMessage = sessionStorage.getItem(
      LOCATION_MAP_REDIRECT_TOAST_KEY
    );

    if (!redirectToastMessage) {
      return;
    }

    sessionStorage.removeItem(LOCATION_MAP_REDIRECT_TOAST_KEY);
    setAppToastMessage(redirectToastMessage);
  }, []);

  const handleAuthSubmit = async (payload) => {
    setIsAuthenticating(true);
    setAuthErrorMessage("");

    try {
      const data = await authenticateMapScanUser(payload);

      if (data.status !== "success") {
        setAuthErrorMessage(data.message || "Unable to continue.");
        return;
      }

      setMapScanToken(data.user.accessToken);
      setIsAuthenticated(true);
      await syncScannedMarkers(data.user.accessToken);
    } catch (error) {
      setAuthErrorMessage("Unable to continue right now.");
    } finally {
      setIsAuthenticating(false);
      setIsCheckingAuth(false);
    }
  };

  const openScannedQrUrl = (qrValue) => {
    if (!qrValue) {
      return;
    }

    try {
      const parsedUrl = new URL(qrValue, window.location.origin);
      const markerIdFromQuery = parsedUrl.searchParams.get("marker") || "";
      const pathSegments = parsedUrl.pathname.split("/").filter(Boolean);
      const modelViewerPathIndex = pathSegments.findIndex(
        (segment) => segment === "modelDynamicviewer"
      );
      const scannedModelId =
        modelViewerPathIndex >= 0 ? pathSegments[modelViewerPathIndex + 1] || "" : "";
      const matchedMarker =
        LOCATION_CONFIG.markers.find((marker) => marker.id === markerIdFromQuery) ||
        getMarkerByModalProductId(scannedModelId) ||
        null;
      const markerId = matchedMarker?.id || markerIdFromQuery;

      if (markerId) {
        const updatedLocalMarkers = markMarkerAsScanned(markerId);
        setScannedMarkerIds((current) => [
          ...new Set([...current, ...updatedLocalMarkers]),
        ]);
      }
    } catch (error) {
      // Ignore URL parsing issues and continue opening the scanned destination.
    }

    const anchor = document.createElement("a");
    anchor.href = qrValue;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    // const newWindow = window.open(qrValue, "_blank", "noopener,noreferrer");
    // if (!newWindow) {
    //   setScanErrorMessage(
    //     "The scanned link was blocked from opening in a new tab. Please allow popups and try again."
    //   );
    // }
  };

  const getQrValueFromBarcodeResults = (results) => results?.[0]?.rawValue || "";

  const decodeQrFromVisual = useCallback((source, width, height) => {
    if (!source || !width || !height) {
      return "";
    }

    if (!scannerCanvasRef.current) {
      scannerCanvasRef.current = document.createElement("canvas");
    }

    const canvas = scannerCanvasRef.current;
    const context = canvas.getContext("2d", { willReadFrequently: true });

    if (!context) {
      return "";
    }

    const decodeAtSize = (targetWidth, targetHeight) => {
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      context.clearRect(0, 0, targetWidth, targetHeight);
      context.drawImage(source, 0, 0, targetWidth, targetHeight);

      const imageData = context.getImageData(0, 0, targetWidth, targetHeight);
      const code = jsQR(imageData.data, targetWidth, targetHeight, {
        inversionAttempts: "attemptBoth",
      });

      return code?.data || "";
    };

    const largestSide = Math.max(width, height);
    const baseScale = largestSide > 1600 ? 1600 / largestSide : 1;
    const decodeScales = [baseScale, baseScale * 0.75, baseScale * 0.5, 1].filter(
      (scale, index, values) =>
        scale > 0 && values.findIndex((value) => Math.abs(value - scale) < 0.01) === index
    );

    for (const scale of decodeScales) {
      const targetWidth = Math.max(320, Math.round(width * scale));
      const targetHeight = Math.max(320, Math.round(height * scale));
      const qrValue = decodeAtSize(targetWidth, targetHeight);

      if (qrValue) {
        return qrValue;
      }
    }

    return "";
  }, []);

  const loadImageElementFromFile = useCallback((file) => {
    const objectUrl = URL.createObjectURL(file);

    const loadFromSource = (src) =>
      new Promise((resolve, reject) => {
        const image = new Image();

        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("Unable to load QR image"));
        image.src = src;
      });

    return new Promise((resolve, reject) => {
      loadFromSource(objectUrl)
        .then((image) => {
          resolve({
            image,
            release: () => URL.revokeObjectURL(objectUrl),
          });
        })
        .catch(() => {
          const reader = new FileReader();

          reader.onload = () => {
            loadFromSource(reader.result)
              .then((image) => {
                URL.revokeObjectURL(objectUrl);
                resolve({
                  image,
                  release: () => {},
                });
              })
              .catch((error) => {
                URL.revokeObjectURL(objectUrl);
                reject(error);
              });
          };

          reader.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error("Unable to read QR image"));
          };

          reader.readAsDataURL(file);
        });
    });
  }, []);

  const readQrValueFromVideoFrame = useCallback(async () => {
    const videoElement = scannerVideoRef.current;

    if (!videoElement || videoElement.readyState < 2) {
      return "";
    }

    if (scannerDetectorRef.current) {
      try {
        const results = await scannerDetectorRef.current.detect(videoElement);
        const detectorValue = getQrValueFromBarcodeResults(results);

        if (detectorValue) {
          return detectorValue;
        }
      } catch (error) {
        scannerDetectorRef.current = null;
      }
    }

    return decodeQrFromVisual(
      videoElement,
      videoElement.videoWidth,
      videoElement.videoHeight
    );
  }, [decodeQrFromVisual]);

  const readQrValueFromFile = useCallback(
    async (file) => {
      let imageBitmap = null;

      try {
        imageBitmap = await createImageBitmap(file);

        if (typeof window.BarcodeDetector !== "undefined") {
          try {
            const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
            const results = await detector.detect(imageBitmap);
            const detectorValue = getQrValueFromBarcodeResults(results);

            if (detectorValue) {
              return detectorValue;
            }
          } catch (error) {
            // Fall back to jsQR below when BarcodeDetector is unavailable or unreliable.
          }
        }

        const bitmapValue = decodeQrFromVisual(
          imageBitmap,
          imageBitmap.width,
          imageBitmap.height
        );

        if (bitmapValue) {
          return bitmapValue;
        }
      } catch (error) {
        // Fall through to HTMLImageElement decoding for wider mobile support.
      } finally {
        imageBitmap?.close?.();
      }

      const { image, release } = await loadImageElementFromFile(file);

      try {
        return decodeQrFromVisual(image, image.naturalWidth, image.naturalHeight);
      } finally {
        release();
      }
    },
    [decodeQrFromVisual, loadImageElementFromFile]
  );

  const handleOpenQrScanner = () => {
    setScanErrorMessage("");

    const canUseLiveScanner = Boolean(navigator.mediaDevices?.getUserMedia);

    if (!canUseLiveScanner) {
      if (!qrInputRef.current) {
        setScanErrorMessage(
          "Live QR scanning is not supported in this browser. Please use your phone camera or a supported mobile browser."
        );
        return;
      }

      qrInputRef.current.value = "";
      qrInputRef.current.click();
      return;
    }

    setIsScannerOpen(true);
    setIsStartingScanner(true);
  };

  const handleResetMapView = useCallback(() => {
    const map = mapInstanceRef.current;

    if (!map || !window.google?.maps) {
      return;
    }

    setSelectedMarkerId("");
    map.setCenter(LOCATION_CONFIG.center);
    map.setZoom(LOCATION_CONFIG.zoom);
    window.google.maps.event.trigger(map, "resize");
  }, []);

  const handleResetScannedState = async () => {
    const token = getMapScanToken();

    if (!token || isResettingScans) {
      return;
    }

    setIsResettingScans(true);
    setScanErrorMessage("");

    try {
      const data = await clearMapScanModals(token);

      if (data.status !== "success") {
        setScanErrorMessage(data.message || "Unable to reset marker state right now.");
        return;
      }

      writeScannedMarkers([]);
      writeListenedAudioMarkers([]);
      setScannedMarkerIds([]);
      setListenedAudioMarkerIds([]);
      setSelectedMarkerId("");
      setAppToastMessage("AR and audio marker state reset.");
    } catch (error) {
      setScanErrorMessage("Unable to reset marker state right now.");
    } finally {
      setIsResettingScans(false);
    }
  };

  const handleLogout = () => {
    clearMapScanToken();
    setIsAuthenticated(false);
    setIsLogoutModalOpen(false);
    setSelectedMarkerId("");
    setScannedMarkerIds(readScannedMarkers());
  };

  const stopScanner = useCallback(() => {
    if (scannerTimeoutRef.current) {
      window.clearTimeout(scannerTimeoutRef.current);
      scannerTimeoutRef.current = null;
    }

    if (scannerStreamRef.current) {
      scannerStreamRef.current.getTracks().forEach((track) => track.stop());
      scannerStreamRef.current = null;
    }

    if (scannerVideoRef.current) {
      scannerVideoRef.current.pause();
      scannerVideoRef.current.srcObject = null;
    }

    setIsStartingScanner(false);
    setIsScannerOpen(false);
  }, []);

  useEffect(() => {
    if (!isScannerOpen) {
      return undefined;
    }

    let isCancelled = false;
    const videoElement = scannerVideoRef.current;

    const scanFrame = async () => {
      if (isCancelled || !scannerVideoRef.current) {
        return;
      }

      try {
        const qrValue = await readQrValueFromVideoFrame();

        if (qrValue) {
          openScannedQrUrl(qrValue);
          stopScanner();
          return;
        }
      } catch (error) {
        setScanErrorMessage(
          "Unable to scan right now. Please try again with better lighting."
        );
      }

      scannerTimeoutRef.current = window.setTimeout(scanFrame, 250);
    };

    const startScanner = async () => {
      try {
        scannerDetectorRef.current =
          typeof window.BarcodeDetector !== "undefined"
            ? new window.BarcodeDetector({
                formats: ["qr_code"],
              })
            : null;

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: {
              ideal: "environment",
            },
          },
        });

        if (isCancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        scannerStreamRef.current = stream;

        if (videoElement) {
          videoElement.srcObject = stream;
          await videoElement.play();
        }

        setIsStartingScanner(false);
        scanFrame();
      } catch (error) {
        setIsStartingScanner(false);
        setIsScannerOpen(false);

        if (qrInputRef.current) {
          qrInputRef.current.value = "";
          qrInputRef.current.click();
          return;
        }

        setScanErrorMessage(
          "Camera access was blocked or unavailable. Please allow camera access and try again."
        );
      }
    };

    startScanner();

    return () => {
      isCancelled = true;

      if (scannerTimeoutRef.current) {
        window.clearTimeout(scannerTimeoutRef.current);
        scannerTimeoutRef.current = null;
      }

      if (scannerStreamRef.current) {
        scannerStreamRef.current.getTracks().forEach((track) => track.stop());
        scannerStreamRef.current = null;
      }

      if (videoElement) {
        videoElement.pause();
        videoElement.srcObject = null;
      }
    };
  }, [isScannerOpen, readQrValueFromVideoFrame, stopScanner]);

  const handleQrFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const qrValue = await readQrValueFromFile(file);

      if (qrValue) {
        openScannedQrUrl(qrValue);
        return;
      }

      setScanErrorMessage("No QR code was detected. Please try again.");
    } catch (error) {
      setScanErrorMessage("Unable to read that QR code. Please try again.");
    } finally {
      event.target.value = "";
    }
  };

  const selectedMarker = selectedMarkerId
    ? visibleMarkers.find((marker) => marker.id === selectedMarkerId)
    : null;
  const sculptureCount = LOCATION_CONFIG.markers.filter(
    (marker) => marker.markerType === "sculpture"
  ).length;
  const selectedMarkerDetail =
    selectedMarker?.markerType === "sculpture" ? selectedMarker : null;
  const selectedMarkerCategory = selectedMarkerDetail
    ? {
        ...ANCHOR_CATEGORIES[selectedMarkerDetail.category],
        color: getSculptureTypeColor(selectedMarkerDetail.sculpture_type),
      }
    : null;
  const audioWaveBars = selectedMarkerDetail
    ? Array.from({ length: 34 }, (_, index) => {
        const seed =
          ((selectedMarkerDetail.number * 131 + 7) * (index + 3) * 9301 + 49297) %
          233280;
        return 22 + Math.round((seed / 233280) * 78);
      })
    : [];
  const activeWaveBars = sheetAudioDuration
    ? Math.round((sheetAudioTime / sheetAudioDuration) * audioWaveBars.length)
    : 0;

  const formatAudioTime = (timeInSeconds) => {
    const safeTime = Math.max(0, Math.round(timeInSeconds || 0));
    return `${Math.floor(safeTime / 60)}:${String(safeTime % 60).padStart(2, "0")}`;
  };

  const closeMarkerSheet = useCallback(() => {
    if (sheetAudioRef.current) {
      sheetAudioRef.current.pause();
      sheetAudioRef.current.currentTime = 0;
    }

    setIsSheetAudioPlaying(false);
    setSheetAudioDuration(0);
    setSheetAudioTime(0);
    setSelectedMarkerId("");
  }, []);

  const handleToggleSheetAudio = useCallback(() => {
    if (!sheetAudioRef.current) {
      return;
    }

    if (isSheetAudioPlaying) {
      sheetAudioRef.current.pause();
      setIsSheetAudioPlaying(false);
      return;
    }

    if (
      sheetAudioDuration &&
      sheetAudioRef.current.currentTime >= sheetAudioDuration
    ) {
      sheetAudioRef.current.currentTime = 0;
    }

    sheetAudioRef.current.play().then(() => {
      setIsSheetAudioPlaying(true);
    }).catch(() => {
      setIsSheetAudioPlaying(false);
    });
  }, [isSheetAudioPlaying, sheetAudioDuration]);

  const handleSheetWaveSeek = useCallback(
    (event) => {
      if (!sheetAudioRef.current || !sheetAudioDuration) {
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const fraction = Math.min(
        1,
        Math.max(0, (event.clientX - rect.left) / rect.width)
      );
      const nextTime = fraction * sheetAudioDuration;
      sheetAudioRef.current.currentTime = nextTime;
      setSheetAudioTime(nextTime);
    },
    [sheetAudioDuration]
  );

  useEffect(() => {
    if (!selectedMarkerDetail || !sheetAudioRef.current) {
      return undefined;
    }

    const audioElement = sheetAudioRef.current;
    audioElement.pause();
    audioElement.currentTime = 0;
    audioElement.load();

    const handleLoadedMetadata = () => {
      setSheetAudioDuration(audioElement.duration || 0);
    };
    const handleTimeUpdate = () => {
      setSheetAudioTime(audioElement.currentTime || 0);
    };
    const handleEnded = () => {
      setIsSheetAudioPlaying(false);
      setSheetAudioTime(audioElement.duration || 0);

      if (selectedMarkerDetail?.id) {
        const updatedMarkers = markAudioMarkerAsListened(selectedMarkerDetail.id);
        setListenedAudioMarkerIds(updatedMarkers);

        const token = getMapScanToken();
        if (token) {
          saveMapScanModal({
            audioListened: true,
            modelId: selectedMarkerDetail.modalProductId,
            scannedTime: new Date().toISOString(),
            token,
            markerId: selectedMarkerDetail.id,
            markerTitle: selectedMarkerDetail.title,
            markerLat: selectedMarkerDetail.position?.lat ?? null,
            markerLng: selectedMarkerDetail.position?.lng ?? null,
          }).catch(() => {});
        }
      }
    };
    const handlePause = () => {
      setIsSheetAudioPlaying(false);
    };
    const handlePlay = () => {
      setIsSheetAudioPlaying(true);
    };

    setIsSheetAudioPlaying(false);
    setSheetAudioDuration(0);
    setSheetAudioTime(0);

    audioElement.addEventListener("loadedmetadata", handleLoadedMetadata);
    audioElement.addEventListener("timeupdate", handleTimeUpdate);
    audioElement.addEventListener("ended", handleEnded);
    audioElement.addEventListener("pause", handlePause);
    audioElement.addEventListener("play", handlePlay);

    return () => {
      audioElement.pause();
      audioElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audioElement.removeEventListener("timeupdate", handleTimeUpdate);
      audioElement.removeEventListener("ended", handleEnded);
      audioElement.removeEventListener("pause", handlePause);
      audioElement.removeEventListener("play", handlePlay);
    };
  }, [selectedMarkerDetail]);

  const layoutStyles = {
    display: "flex",
    flex: 1,
    gap: "0",
    minHeight: 0,
    height: "100%",
    margin: "0",
    maxWidth: "100%",
    width: "100%",
  };
  const mapCardStyles = {
    display: "flex",
    flex: 1,
    border: 0,
    borderRadius: "0",
    boxShadow: "none",
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
    position: "relative",
  };
  const mapCanvasStyles = {
    flex: 1,
    height: usePhoneLayout
      ? isCompactViewport
        ? "100%"
        : "616px"
      : "calc(100vh - 225px)",
    minHeight: usePhoneLayout
      ? isCompactViewport
        ? 0
        : "616px"
      : "572px",
    position: "relative",
    width: "100%",
  };

  if (isDesktopViewport) {
    return (
      <MapScanAuthModal
        desktopPromoImage={locationMapDesktopLanding}
        desktopPromoMessage="For better experience open this link on your mobile."
        errorMessage=""
        isLoading={false}
        onSubmit={() => {}}
      />
    );
  }

  if (isCheckingAuth) {
    return <div style={styles.loading}>Loading...</div>;
  }

  if (!isAuthenticated) {
    return (
      <MapScanAuthModal
        desktopPromoImage={locationMapDesktopLanding}
        desktopPromoMessage="For better experience open this link on your mobile."
        errorMessage={authErrorMessage}
        isLoading={isAuthenticating}
        onSubmit={handleAuthSubmit}
        subtitle="Enter your details to access the location map and scanned models."
        title="Let's begin."
      />
    );
  }

  return (
    <>
      {isScannerOpen ? (
        <div style={styles.scannerOverlay}>
          <div style={styles.scannerTopBar}>
            <button
              onClick={stopScanner}
              style={styles.scannerCloseButton}
              type="button"
            >
              Close
            </button>
            <div>
              <p style={styles.scannerEyebrow}>Augmented Reality</p>
              <strong style={styles.scannerTitle}>Scan to view in AR</strong>
            </div>
            <div style={styles.scannerTopBarSpacer} />
          </div>
          <div style={styles.scannerFrame}>
            <video
              autoPlay
              muted
              playsInline
              ref={scannerVideoRef}
              style={styles.scannerVideo}
            />
            <div style={styles.scannerGuideBox} />
            {isStartingScanner ? (
              <div style={styles.scannerHintWrap}>
                <p style={styles.scannerHintText}>
                  Point at the AR marker on any sculpture plaque
                </p>
                <div style={styles.scannerHintSubtle}>⌖ looking for markers...</div>
              </div>
            ) : (
              <div style={styles.scannerHintWrap}>
                <p style={styles.scannerHintText}>
                  Point at the AR marker on any sculpture plaque
                </p>
                <div style={styles.scannerHintSubtle}>⌖ looking for markers...</div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {isLogoutModalOpen ? (
        <div className="anchor-map-confirm-scrim">
          <div className="anchor-map-confirm-card">
            <h3>Are you sure you want to logout?</h3>
            <p>You will be sent back to the location-map login page.</p>
            <div className="anchor-map-confirm-actions">
              <button
                className="anchor-map-confirm-btn anchor-map-confirm-btn-secondary"
                onClick={() => setIsLogoutModalOpen(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="anchor-map-confirm-btn anchor-map-confirm-btn-danger"
                onClick={handleLogout}
                type="button"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="anchor-map-page">
        <div className="anchor-map-shell-wrap">
          <div className="anchor-map-brandline">
            <span className="anchor-map-brand-dot" />
            <span>ANCHORBALL</span>
            <span>A CURIOUS ART JOURNEY</span>
          </div>
          <div className="anchor-map-phone">
            <div className="anchor-map-shell">
              <header className="anchor-map-appbar">
                <div className="anchor-map-appbar-top">
                  <div className="anchor-map-logo">
                    ANCH
                    <span className="anchor-map-logo-o">
                      <svg fill="none" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="11" stroke="#16202b" strokeWidth="2" />
                        <rect fill="#16202b" height="3.4" width="3.4" x="7" y="7" />
                        <rect fill="#16202b" height="3.4" width="3.4" x="13.6" y="7" />
                        <rect fill="#16202b" height="3.4" width="3.4" x="7" y="13.6" />
                        <rect fill="#16202b" height="3.4" width="3.4" x="13.6" y="13.6" />
                      </svg>
                    </span>
                    RBALL
                  </div>
                  <div className="anchor-map-appbar-actions">
                    <button
                      className="anchor-map-pillbtn"
                      onClick={() =>
                        setAppToastMessage("Tap a pin for its audio guide · scan markers for AR.")
                      }
                      type="button"
                    >
                      <svg fill="none" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 16v-4M12 8h.01" />
                      </svg>
                    </button>
                    <button
                      className="anchor-map-pillbtn"
                      onClick={handleResetScannedState}
                      type="button"
                    >
                      <svg fill="none" viewBox="0 0 24 24">
                        <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                        <path d="M21 3v6h-6" />
                      </svg>
                    </button>
                    <button
                      className="anchor-map-pillbtn"
                      onClick={() => setIsLogoutModalOpen(true)}
                      type="button"
                    >
                      <svg fill="none" viewBox="0 0 24 24">
                        <path d="M12 3v10" />
                        <path d="M7.05 5.05a9 9 0 1 0 9.9 0" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="anchor-map-subbar">
                  <span className="anchor-map-subbar-item">
                    <span className="anchor-map-subbar-dot" />
                    {sculptureCount} sculptures
                  </span>
                  <span className="anchor-map-subbar-sep" />
                  <span className="anchor-map-subbar-item">
                    Tap a pin to listen · scan for AR
                  </span>
                </div>
              </header>

              <div className="anchor-map-content">
                {scanErrorMessage ? (
                  <div className="anchor-map-inline-error">
                    {scanErrorMessage ? <p>{scanErrorMessage}</p> : null}
                  </div>
                ) : null}

                <div style={layoutStyles}>
                  <div style={mapCardStyles}>
                    <div ref={mapRef} style={mapCanvasStyles} />
                  </div>
                </div>
              </div>

              <div
                className={`anchor-map-sheet-scrim${
                  selectedMarkerDetail ? " show" : ""
                }`}
                onClick={closeMarkerSheet}
              />
              <div
                className={`anchor-map-sheet${
                  selectedMarkerDetail ? " show" : ""
                }`}
              >
                {selectedMarkerDetail ? (
                  <>
                    <div className="anchor-map-sheet-handle" />
                    <div className="anchor-map-sheet-head">
                      <div
                        className="anchor-map-sheet-thumb"
                        style={{
                          background: selectedMarkerCategory?.color || "#2ea3e0",
                        }}
                      >
                        {selectedMarkerDetail.imageSrc ? (
                          <img
                            src={selectedMarkerDetail.imageSrc}
                            alt={selectedMarkerDetail.title}
                            className="anchor-map-sheet-thumb-image"
                          />
                        ) : null}
                      </div>
                      <div className="anchor-map-sheet-info">
                        <span
                          className="anchor-map-sheet-cat"
                          style={{
                            background: selectedMarkerCategory?.color || "#2ea3e0",
                          }}
                        >
                          <span className="anchor-map-sheet-cat-dot">●</span>
                          {selectedMarkerDetail?.sculpture_type || "Wonder Sculpture"}
                        </span>
                        <h3>{selectedMarkerDetail.title}</h3>
                        {/* <div className="anchor-map-sheet-num">
                          SCULPTURE NO.{" "}
                          {String(selectedMarkerDetail.number).padStart(2, "0")} / 17
                        </div> */}
                      </div>
                    </div>
                    <p className="anchor-map-sheet-desc">
                      {selectedMarkerDetail.describe ? selectedMarkerDetail.describe : selectedMarkerDetail.description}
                    </p>
                    <div
                      className={`anchor-map-audio${
                        isSheetAudioPlaying ? " playing" : ""
                      }`}
                    >
                      <button
                        className="anchor-map-audio-play"
                        onClick={handleToggleSheetAudio}
                        type="button"
                      >
                        <svg
                          className="ic-play"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                        <svg
                          className="ic-pause"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <rect x="6" y="5" width="4" height="14" rx="1.2" />
                          <rect x="14" y="5" width="4" height="14" rx="1.2" />
                        </svg>
                      </button>
                      <div className="anchor-map-audio-body">
                        <div className="anchor-map-audio-top">
                          <span className="anchor-map-audio-label">
                            AUDIO GUIDE · {selectedMarkerDetail.title.toUpperCase()}
                          </span>
                          <span className="anchor-map-audio-time">
                            <span className="anchor-map-audio-current">
                              {formatAudioTime(sheetAudioTime)}
                            </span>{" "}
                            / {formatAudioTime(sheetAudioDuration)}
                          </span>
                        </div>
                        <div
                          className="anchor-map-audio-wave"
                          onClick={handleSheetWaveSeek}
                        >
                          {audioWaveBars.map((height, index) => (
                            <div
                              className={`anchor-map-audio-bar${
                                index < activeWaveBars ? " on" : ""
                              }`}
                              key={`${selectedMarkerDetail.id}-${index}`}
                              style={{ height: `${height}%` }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="anchor-map-audio-sub">
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M11 5 6 9H2v6h4l5 4V5Z" />
                        <path d="M15.5 8.5a5 5 0 0 1 0 7M19 5a9 9 0 0 1 0 14" />
                      </svg>
                      Narrated story by artist Ken Kelleher (ANCHORBALL)
                      Music provided by Uppbeat
                    </div>
                    <audio ref={sheetAudioRef} preload="metadata">
                      <source src={selectedMarkerDetail.audioSrc} type="audio/mpeg" />
                    </audio>
                  </>
                ) : null}
              </div>

              {appToastMessage ? (
                <div className="anchor-map-toast">
                  <span className="anchor-map-toast-dot" />
                  <span>{appToastMessage}</span>
                </div>
              ) : null}

              <nav className="anchor-map-tabbar">
                <button
                  className="anchor-map-tab anchor-map-tab-active"
                  onClick={handleResetMapView}
                  type="button"
                >
                  <svg fill="none" viewBox="0 0 24 24">
                    <path d="m9 4-6 2v15l6-2 6 2 6-2V4l-6 2-6-2Z" />
                    <path d="M9 4v15M15 6v15" />
                  </svg>
                  <span>Map</span>
                </button>
                <button
                  className="anchor-map-tab anchor-map-tab-ar"
                  onClick={handleOpenQrScanner}
                  type="button"
                >
                  <span className="anchor-map-ar-icon">
                    <svg fill="none" viewBox="0 0 24 24">
                      <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" />
                      <path d="M12 8.5 8 10.7v4.6l4 2.2 4-2.2v-4.6L12 8.5Z" />
                      <path d="m12 8.5 4 2.2M12 8.5l-4 2.2M12 17.5V13" />
                    </svg>
                  </span>
                  <span>Scan to view in AR</span>
                </button>
              </nav>

              <input
                accept="image/*"
                capture="environment"
                onChange={handleQrFileChange}
                ref={qrInputRef}
                style={{ display: "none" }}
                type="file"
              />
            </div>
          </div>
        </div>
      </div>

    </>
  );
}

const styles = {
  page: {
    background: "#f8fafc",
    minHeight: "100vh",
    padding: "32px 20px 48px",
  },
  hero: {
    alignItems: "flex-start",
    display: "flex",
    flexWrap: "wrap",
    gap: "16px",
    justifyContent: "space-between",
    margin: "0 auto 24px",
    maxWidth: "1280px",
  },
  eyebrow: {
    color: "#0f766e",
    fontSize: "13px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    margin: "0 0 8px",
    textTransform: "uppercase",
  },
  title: {
    color: "#0f172a",
    fontSize: "36px",
    fontWeight: 800,
    margin: "0 0 12px",
  },
  subtitle: {
    color: "#475569",
    fontSize: "16px",
    lineHeight: 1.6,
    margin: 0,
    maxWidth: "720px",
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
  },
  primaryButton: {
    background: "#0f766e",
    borderRadius: "999px",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: 700,
    padding: "12px 18px",
    textDecoration: "none",
  },
  secondaryButton: {
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    borderRadius: "999px",
    color: "#0f172a",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 700,
    padding: "12px 18px",
  },
  scanBanner: {
    background: "linear-gradient(135deg, #ecfeff 0%, #f8fafc 100%)",
    border: "1px solid #bae6fd",
    borderRadius: "20px",
    display: "grid",
    gap: "4px",
    margin: "0 auto 20px",
    maxWidth: "1280px",
    padding: "16px 18px",
  },
  scanBannerLabel: {
    color: "#0f766e",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  scanBannerTitle: {
    color: "#0f172a",
    fontSize: "18px",
  },
  scanBannerText: {
    color: "#475569",
    fontSize: "14px",
    lineHeight: 1.5,
  },
  layout: {
    display: "grid",
    gap: "24px",
    gridTemplateColumns: "minmax(0, 2fr) minmax(320px, 420px)",
    margin: "0 auto",
    maxWidth: "1280px",
  },
  mapCard: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "24px",
    boxShadow: "0 18px 50px rgba(15, 23, 42, 0.08)",
    overflow: "hidden",
  },
  mapCanvas: {
    height: "70vh",
    minHeight: "520px",
    width: "100%",
  },
  panel: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "24px",
    boxShadow: "0 18px 50px rgba(15, 23, 42, 0.08)",
    padding: "20px",
  },
  panelHeader: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "16px",
  },
  panelTitle: {
    color: "#0f172a",
    fontSize: "22px",
    fontWeight: 800,
    margin: 0,
  },
  counter: {
    background: "#ecfeff",
    borderRadius: "999px",
    color: "#155e75",
    fontSize: "12px",
    fontWeight: 700,
    padding: "8px 12px",
  },
  markerCard: {
    border: "1px solid #e2e8f0",
    borderRadius: "18px",
    marginBottom: "14px",
    padding: "16px",
  },
  markerRow: {
    alignItems: "flex-start",
    display: "flex",
    gap: "12px",
    marginBottom: "12px",
  },
  markerDot: {
    borderRadius: "999px",
    boxShadow: "0 0 0 5px rgba(15, 23, 42, 0.06)",
    display: "inline-block",
    flexShrink: 0,
    height: "14px",
    marginTop: "6px",
    width: "14px",
  },
  markerTitle: {
    color: "#0f172a",
    fontSize: "16px",
    fontWeight: 800,
    margin: "0 0 6px",
  },
  markerDescription: {
    color: "#64748b",
    fontSize: "14px",
    lineHeight: 1.5,
    margin: 0,
  },
  badgeRow: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  statusBadge: {
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 700,
    padding: "7px 12px",
  },
  linkButton: {
    background: "transparent",
    border: 0,
    color: "#0f766e",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 700,
    padding: 0,
  },
  qrLabel: {
    color: "#334155",
    fontSize: "12px",
    fontWeight: 700,
    margin: "0 0 6px",
    textTransform: "uppercase",
  },
  qrUrl: {
    color: "#475569",
    fontSize: "12px",
    lineHeight: 1.6,
    margin: 0,
    wordBreak: "break-all",
  },
  qrPreview: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    display: "inline-flex",
    marginTop: "12px",
    padding: "10px",
  },
  coordinateText: {
    color: "#475569",
    fontSize: "12px",
    lineHeight: 1.6,
    margin: "10px 0 0",
  },
  errorText: {
    color: "#b91c1c",
    fontSize: "14px",
    margin: "12px 16px 16px",
  },
  mobileSelectedCard: {
    background: "rgba(15, 23, 42, 0.82)",
    borderRadius: "18px",
    bottom: "96px",
    color: "#ffffff",
    display: "grid",
    gap: "4px",
    left: "12px",
    maxWidth: "calc(100% - 24px)",
    padding: "12px 14px",
    position: "fixed",
    zIndex: 5,
  },
  mobileSelectedTitle: {
    fontSize: "15px",
    fontWeight: 800,
  },
  mobileSelectedText: {
    color: "#cbd5e1",
    fontSize: "12px",
    lineHeight: 1.5,
  },
  mobileScanBar: {
    background:
      "linear-gradient(180deg, rgba(248,250,252,0) 0%, rgba(248,250,252,0.95) 28%, #f8fafc 100%)",
    bottom: 0,
    left: 0,
    padding: "18px 12px 22px",
    position: "fixed",
    right: 0,
    zIndex: 6,
  },
  mobileScanButton: {
    background: "#0f766e",
    border: 0,
    borderRadius: "999px",
    color: "#ffffff",
    cursor: "pointer",
    display: "block",
    fontSize: "16px",
    fontWeight: 800,
    minHeight: "54px",
    padding: "14px 18px",
    width: "100%",
  },
  scannerOverlay: {
    background: "#020617",
    inset: 0,
    position: "fixed",
    zIndex: 20,
  },
  scannerTopBar: {
    alignItems: "center",
    color: "#ffffff",
    display: "flex",
    justifyContent: "space-between",
    left: 0,
    padding: "18px 16px 12px",
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 22,
  },
  scannerTopBarSpacer: {
    width: "70px",
  },
  scannerEyebrow: {
    color: "rgba(255,255,255,0.72)",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.3em",
    margin: "0 0 4px",
    textTransform: "uppercase",
    textAlign: "center",
  },
  scannerTitle: {
    display: "block",
    fontFamily: '"Archivo", sans-serif',
    fontSize: "18px",
    fontWeight: 800,
    textAlign: "center",
  },
  scannerCloseButton: {
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: "999px",
    color: "#ffffff",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 700,
    minHeight: "40px",
    padding: "10px 14px",
  },
  scannerFrame: {
    alignItems: "center",
    display: "flex",
    height: "100vh",
    justifyContent: "center",
    overflow: "hidden",
    position: "relative",
    width: "100%",
  },
  scannerVideo: {
    height: "100%",
    objectFit: "cover",
    width: "100%",
  },
  scannerGuideBox: {
    border: "3px solid rgba(255,255,255,0.92)",
    borderRadius: "24px",
    boxShadow: "0 0 0 9999px rgba(2, 6, 23, 0.42)",
    height: "250px",
    left: "50%",
    maxHeight: "56vw",
    maxWidth: "56vw",
    pointerEvents: "none",
    position: "absolute",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: "250px",
  },
  scannerHintWrap: {
    bottom: "42px",
    color: "#ffffff",
    left: 0,
    position: "absolute",
    right: 0,
    textAlign: "center",
    zIndex: 21,
  },
  scannerHintText: {
    fontSize: "13px",
    margin: 0,
    opacity: 0.85,
  },
  scannerHintSubtle: {
    fontFamily: '"Space Mono", monospace',
    fontSize: "11px",
    marginTop: "8px",
    opacity: 0.55,
  },
  scannerHint: {
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: 600,
    left: "50%",
    position: "absolute",
    transform: "translateX(-50%)",
    whiteSpace: "nowrap",
    zIndex: 21,
  },
  loading: {
    alignItems: "center",
    color: "#0f172a",
    display: "flex",
    fontSize: "18px",
    justifyContent: "center",
    minHeight: "100vh",
  },
};
